import './NewModalLayoutComponentConfig';
import { useContext, ReactNode, RefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultModalHeader from './components/DefaultModalHeader/DefaultModalHeader';
import {
  DialogContentStyled,
  HeaderContainerStyled,
  FooterContainerStyled,
} from './styles/NewModalLayoutComponentStyles';
import { ModalHeaderTitleEnum } from '../../../constants/enums/ModalHeaderTitleEnum';
import { ModalContext } from '../../../contexts/ModalContext';
import AtomProgress from '../../atoms/AtomProgress';

export type skillStatusType = 'backlog' | 'learning' | 'completed';
export type modalHeaderTitleType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface IModalLayoutProps {
  activeHeader?: boolean;
  titleHeader?: ReactNode | string;
  centerTitle?: boolean;
  fontSizeTitleHeader?: modalHeaderTitleType;
  activeJobTitleHeader?: boolean;
  jobTitleHeader?: string;
  customHeader?: ReactNode;
  skillStatus?: skillStatusType;
  scrollRef?: RefObject<HTMLDivElement>;
  loading?: boolean;
  children: ReactNode;
  customFooter?: ReactNode;
  paddingInline?: number | string;
  paddingBlock?: number | string;
  hasDivider?: boolean;
  onBackButton?: VoidFunction;
  customModalClose?: VoidFunction;
  activePaddingContent?: boolean;
  disableCloseButton?: boolean;
}

const NewModalLayoutComponent: React.FC<IModalLayoutProps> = (props) => {
  const {
    activeHeader = true,
    titleHeader = '',
    centerTitle,
    fontSizeTitleHeader = ModalHeaderTitleEnum.H2,
    activeJobTitleHeader,
    jobTitleHeader,
    customHeader,
    skillStatus,
    scrollRef,
    loading,
    children,
    customFooter,
    paddingInline,
    paddingBlock,
    hasDivider,
    onBackButton,
    customModalClose,
    activePaddingContent = true,
    disableCloseButton,
  } = props;
  const { t } = useTranslation('NewModalLayoutComponent');
  const { modalClose } = useContext(ModalContext);

  const handleDisableCloseButton = useCallback((): VoidFunction | undefined => {
    if (disableCloseButton) {
      return undefined;
    }

    return customModalClose ?? modalClose;
  }, [disableCloseButton, customModalClose, modalClose]);

  return (
    <>
      <HeaderContainerStyled hasDivider={hasDivider ? 1 : 0}>
        {activeHeader &&
          (customHeader ?? (
            <DefaultModalHeader
              title={titleHeader}
              centerTitle={centerTitle}
              fontSizeTitleHeader={fontSizeTitleHeader}
              activeJobTitle={activeJobTitleHeader}
              jobTitle={jobTitleHeader}
              paddingInline={paddingInline}
              paddingBlock={paddingBlock}
              onBackButton={onBackButton}
              skillStatus={skillStatus}
              modalClose={handleDisableCloseButton()}
              ariaLabels={{
                backButton: t('BACK_ARIA_LABEL'),
                closeButton: t('CLOSE_ARIA_LABEL'),
              }}
            />
          ))}
      </HeaderContainerStyled>
      <DialogContentStyled ref={scrollRef} activePaddingContent={activePaddingContent}>
        <AtomProgress
          visible={loading}
          layout="inline"
          background="transparent"
          loadingText={t('LOADER_TEXT')}
          textColor="prussianBlue"
          data-testid="circular_loader"
        />
        {children}
      </DialogContentStyled>
      {customFooter && <FooterContainerStyled hasDivider={hasDivider ? 1 : 0}>{customFooter}</FooterContainerStyled>}
    </>
  );
};

export default NewModalLayoutComponent;
